import React from 'react';
import { Form, Input, Checkbox, Alert, Tooltip } from 'antd';
import { QuestionCircleOutlined } from '@ant-design/icons';
import { NameField } from '../../cloudAccountFields';
import { TestConnectionButton } from '../../cloudAccountFields';
import TagsSelector from '../../../../TagsSelector';
import { getHelpLink } from '../../utils/cloudAccountHelpers';
import './GCPCloudAccount.scss';

/**
 * GCP Cloud Account - Edit Mode
 * Focused component for editing existing GCP cloud accounts
 */
export const GCPCloudAccountEdit = ({
    formData,
    errors,
    onChange,
    onTestConnection,
    testing,
    tested,
    onDownloadTemplate,
    canEditVSphereCredentials,
    showEditVSphereCredentials,
    onToggleVSphereEdit,
    vsphereCredentials,
    onVSphereCredentialChange,
    tags,
    onTagsChange,
    userOrg,
}) => {
    return (
        <>
            <NameField value={formData.name} onChange={onChange('name')} error={errors.name} />

            {/* Credentials Warning */}
            <Alert
                title="Only update credentials if they have changed."
                type="warning"
                showIcon
                icon={<QuestionCircleOutlined />}
                className="gcp-credentials-alert"
            />

            {/* Google Service Account Field */}
            <Form.Item
                label={
                    <span>
                        Google Service Account{' '}
                        <Tooltip title="Enter the Google Service Account email associated with the project.">
                            <QuestionCircleOutlined />
                        </Tooltip>
                    </span>
                }
                validateStatus={errors.username ? 'error' : ''}
                help={errors.username}
            >
                <Input
                    value={formData.username || ''}
                    onChange={onChange('username')}
                    placeholder="Enter Google Service Account (optional)"
                    className={formData.username ? 'gcp-modified-input' : ''}
                />
            </Form.Item>

            {/* GCP Helper Links in Edit Mode */}
            <div className="gcp-helper-links-edit">
                <a
                    href={getHelpLink('GCP_APPLIANCE_DOCS')}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="gcp-link"
                >
                    View Instructions
                </a>
                <a
                    href="#"
                    onClick={(e) => {
                        e.preventDefault();
                        onDownloadTemplate('gcp');
                    }}
                    className="gcp-link"
                >
                    Download Template
                </a>
                <a
                    href="#"
                    onClick={(e) => {
                        e.preventDefault();
                        onDownloadTemplate('gcp', 'shared_vpc');
                    }}
                    className="gcp-link"
                >
                    Download Shared VPC Template
                </a>
            </div>

            {/* Test Connection - Required for GCP */}
            <TestConnectionButton
                onTest={onTestConnection}
                testing={testing}
                tested={tested}
                tooltip="Connection must be validated before you can Add Target Cloud. The connection and login credentials are tested (SSL certification is ignored)."
                required={false}
            />

            {/* vSphere Credentials Edit */}
            {canEditVSphereCredentials && (
                <>
                    <Form.Item>
                        <Checkbox
                            checked={showEditVSphereCredentials}
                            onChange={(e) => onToggleVSphereEdit(e.target.checked)}
                        >
                            Edit Source vSphere Environment Credentials
                        </Checkbox>
                    </Form.Item>

                    {showEditVSphereCredentials && (
                        <div className="gcp-vsphere-credentials-box">
                            <h5 className="gcp-vsphere-credentials-heading">Edit Credentials</h5>

                            <Form.Item label="Username">
                                <Input
                                    value={vsphereCredentials.username}
                                    onChange={(e) => onVSphereCredentialChange('username', e.target.value)}
                                    placeholder="Enter vSphere Username"
                                />
                            </Form.Item>

                            <Form.Item label="Password">
                                <Input.Password
                                    value={vsphereCredentials.password}
                                    onChange={(e) => onVSphereCredentialChange('password', e.target.value)}
                                    placeholder="Enter vSphere Password"
                                />
                            </Form.Item>
                        </div>
                    )}
                </>
            )}

            {/* Tags */}
            <Form.Item label="Tags">
                <TagsSelector value={tags} onChange={onTagsChange} userOrg={userOrg} />
            </Form.Item>
        </>
    );
};
